import React, { useMemo } from 'react';
import { CharacterPortrait, DialogueItem } from '../../types';
import { PLACEMENT_X, resolveMoodImage, CharacterPlacement } from '../../systems/character-runtime';
import { splitCharacterDisplayName } from '../../systems/ally-trust';
import { FormattedDialogueText, getDialogueVisibleText } from '../formatted-dialogue-text';
import { assetUrl } from './scenario-api';
import dialogueStyles from '../dialogue-box.module.css';
import styles from './scenario-studio.module.css';

interface DialoguePreviewProps {
  scenarioId: string;
  background?: string;
  item: DialogueItem;
  characters: CharacterPortrait[];
  placement: CharacterPlacement;
  currentEmotion: string;
}

export const DialoguePreview: React.FC<DialoguePreviewProps> = ({
  scenarioId,
  background,
  item,
  characters,
  placement,
  currentEmotion,
}) => {
  const selectedChar = item.characterId
    ? characters.find((c) => c.id === item.characterId)
    : undefined;
  const spriteImage = selectedChar ? resolveMoodImage(selectedChar, currentEmotion) : undefined;
  const speaker = item.speaker?.trim() || selectedChar?.name;
  const visibleText = useMemo(() => getDialogueVisibleText(item.text), [item.text]);
  const placementLabel = placement.charAt(0).toUpperCase() + placement.slice(1);
  const isInternal = Boolean(item.isInternal);
  const nameplate = isInternal
    ? null
    : item.characterId
      ? splitCharacterDisplayName(speaker || item.characterId)
      : speaker
        ? splitCharacterDisplayName(speaker)
        : { givenName: 'Vous', role: '' };

  return (
    <div className={styles.dialoguePreviewSection}>
      <div className={styles.dialoguePreviewHeader}>
        <h5>Aperçu en jeu</h5>
        {!item.isInternal && selectedChar && (
          <span className={styles.dialoguePreviewPlacementBadge}>
            Placement : {placementLabel}
          </span>
        )}
      </div>
      <div
        className={styles.dialoguePreview}
        style={{
          backgroundImage: background
            ? `url(${assetUrl(scenarioId, background)})`
            : undefined,
        }}
      >
        {!item.isInternal && selectedChar && spriteImage && (
          <div
            className={styles.dialoguePreviewCharacter}
            style={{ left: `${PLACEMENT_X[placement]}%` }}
          >
            <img src={assetUrl(scenarioId, spriteImage)} alt={selectedChar.name} />
          </div>
        )}

        <div
          className={[
            dialogueStyles.dialogueBox,
            isInternal && dialogueStyles.internalDialogue,
            styles.dialoguePreviewBox,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {nameplate && (
            <div className={dialogueStyles.speakerChip}>
              <span className={dialogueStyles.speakerGiven}>{nameplate.givenName}</span>
              {nameplate.role && (
                <span className={dialogueStyles.speakerRole}>{nameplate.role}</span>
              )}
            </div>
          )}
          <div
            className={`${dialogueStyles.dialogueText} ${isInternal ? dialogueStyles.internalText : ''}`}
          >
            {isInternal && (
              <svg
                className={dialogueStyles.thoughtIcon}
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M7 17c-2.2 0-4-1.7-4-3.9 0-1.7 1.1-3.2 2.7-3.7C5.9 6.9 8 5 10.6 5c2.4 0 4.4 1.6 4.9 3.8h.3c2.3 0 4.2 1.8 4.2 4.1S18.1 17 15.8 17Z" />
                <circle cx="5.5" cy="19.5" r="1.3" fill="currentColor" stroke="none" />
              </svg>
            )}
            <div>
              {visibleText ? (
                <FormattedDialogueText text={item.text} visibleLength={visibleText.length} />
              ) : (
                <span className={styles.dialoguePreviewPlaceholder}>
                  Saisissez le texte du dialogue…
                </span>
              )}
            </div>
          </div>
          {visibleText && <span className={dialogueStyles.continueChevron} aria-hidden />}
        </div>
      </div>
    </div>
  );
};
